import React, { useContext, useEffect, useState } from 'react'
import DatePicker from '@embracesbs/component-datepicker'
import TextFieldWrapper from '@embracesbs/component-textfieldwrapper'
import Button2 from '@embracesbs/component-button2'
import Text from '@embracesbs/component-text'
import { customToast } from '@embracesbs/component-toast'
import { InputField } from '../StyledComponents/Components'
import { AuthRoleContext } from '../Auth/AuthRoleContext'
import { useTenants } from '../Context/TenantContext'
import { useApi } from '../../Hooks'
import { apiErrorMessage, clientSecretExpiryStatus, clientSecretExpiryText, startOfLocalDayUtcIso } from '../../Utilities/helpers/helpers'

interface ClientSecretExpiryFieldProps {
  tenantId: string
  expiresOnUtc: string | null
  // A date left over after switching away from AzureAd still drives the list warning, so it
  // stays visible here with only a Clear.
  clearOnly?: boolean
}

const statusColor = (state: string) => (state === 'expired' || state === 'expiring' ? 'var(--tma-status-failed)' : 'var(--tma-text-muted)')

// Saved through its own endpoint, never with the Full/Suite update, so it is editable in any
// update mode and while a deploy runs.
const ClientSecretExpiryField: React.FC<ClientSecretExpiryFieldProps> = ({ tenantId, expiresOnUtc, clearOnly = false }) => {
  const { fetchApi } = useApi()
  const { setTenantClientSecretExpiry } = useTenants()
  const roles = useContext(AuthRoleContext)
  // The endpoint authorizes tenant.write only; tenant.admin on its own is refused.
  const canEdit = !!roles?.isWriter

  const [picked, setPicked] = useState<Date | undefined>(expiresOnUtc ? new Date(expiresOnUtc) : undefined)
  const [isSaving, setIsSaving] = useState(false)

  // Keyed on the instant, not the string: the saved "…:00.000Z" and the polled "…:00Z" are
  // the same date and must not reset the picker.
  const storedMs = expiresOnUtc ? new Date(expiresOnUtc).getTime() : null
  useEffect(() => {
    setPicked(storedMs !== null ? new Date(storedMs) : undefined)
  }, [storedMs, tenantId])

  const pickedUtc = picked ? startOfLocalDayUtcIso(picked) : null
  const stored = clientSecretExpiryStatus(expiresOnUtc)
  const shown = clientSecretExpiryStatus(pickedUtc)
  // Compared as local dates: a stored instant that is not this viewer's local midnight (saved
  // from another timezone, or through the API) is still the same day, not an edit.
  const isDirty = shown.localDate !== stored.localDate

  const save = (value: string | null) => {
    setIsSaving(true)
    fetchApi({
      endpoint: `/api/tenant/${tenantId}/clientsecretexpiry`,
      method: 'PUT',
      body: { expiresOnUtc: value }
    })
      .then(() => {
        setTenantClientSecretExpiry(tenantId, value)
        customToast.success(value ? 'Client secret expiry saved' : 'Client secret expiry cleared')
      })
      .catch((error) => {
        console.error(error)
        customToast.error(apiErrorMessage(error, 'Could not save the client secret expiry'))
      })
      .finally(() => setIsSaving(false))
  }

  const statusText = clientSecretExpiryText(clearOnly ? stored : shown)

  return (
    <TextFieldWrapper labelText='Client secret expires on' style={{ flex: 1 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
        <InputField readOnly placeholder='Not recorded' value={(clearOnly ? stored : shown).localDate ?? ''} style={{ flex: 1 }} />
        {!clearOnly && <DatePicker selectedDay={picked} onSelectingDay={(day: Date) => setPicked(day)} />}
        {!clearOnly && (
          <Button2
            text='Save'
            variant='contained'
            type='button'
            isDisabled={!canEdit || isSaving || !picked || !isDirty}
            onClick={() => save(pickedUtc)}
          />
        )}
        <Button2
          text='Clear'
          variant='outlined'
          type='button'
          isDisabled={!canEdit || isSaving || !expiresOnUtc}
          onClick={() => save(null)}
        />
      </div>
      {statusText && (
        <Text textStyle='description' content={statusText} style={{ color: statusColor((clearOnly ? stored : shown).state) }} />
      )}
      <Text
        textStyle='description'
        textColor='grey'
        content={
          clearOnly
            ? 'Only used with Authentication Type AzureAd. Clear it to remove the warning.'
            : 'Saved immediately — not part of the update.'
        }
      />
    </TextFieldWrapper>
  )
}

export default ClientSecretExpiryField
